import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    activityStateEnum,
    activityTypeEnum,
    getActivityStateLabel,
    getTruckModeLabel,
    permissionLevelEnum,
    reportStateEnum,
    stepModeEnum,
} from '../../../../constants';
import { getActivityStateColor } from '../../../../constants/colors';
import {
    useActivitiesStateMutation,
    useDeleteActivityMutation,
    useImportActivitiesBySiteMutation,
    useUpdateActivityMutation,
} from '../../../../hooks/mutations/useActivityMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { useBoolean } from '../../../../hooks/useBoolean';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { ActivityType } from '../../../../services/apiSLTP/dailyReports/activityTypes';
import { Truck } from '../../../../services/apiSLTP/materials/trucks';
import { Site } from '../../../../services/apiSLTP/sites/sites';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatDateToDDMMYYYY, formatTime } from '../../../../utils/dates';
import ActivityNeedsCell from '../../../cells/ActivityNeedsCell';
import MachinesCell from '../../../cells/MachinesCell';
import PersonNeedsCell from '../../../cells/PersonNeedsCell';
import RentalsCell from '../../../cells/RentalsCell';
import StartTimeCell from '../../../cells/StartTimeCell';
import StateCell from '../../../cells/StateCell';
import SubContractorsCell from '../../../cells/SubContractorsCell';
import TempWorkersCell from '../../../cells/TempWorkersCell';
import ToolsCell from '../../../cells/ToolsCell';
import TrucksPlannedCell from '../../../cells/TrucksPlannedCell';
import TruckIcon from '../../../domain/TruckIcon';
import CreateActivityNeedsModal from '../../../modals/operations/needs/CreateActivityNeedsModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface NeedsGlobalTableProps {
    currentDate: Date;
    activities: DataItem[];
    refetch: () => void;
    isLoading: boolean;
    dataTrucks: Truck[];
    dataActivitiesTypes: ActivityType[];
    dataUsers: User[];
    checkEditableRow: (activity: DataItem) => boolean;
    isUsersLoading: boolean;
    isActivitiesTypesLoading: boolean;
    site: Site;
    siteRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
    aggridRefs: React.MutableRefObject<Record<string, AgGridReact<any> | null>>;
}

const NeedsGlobalTable: React.FC<NeedsGlobalTableProps> = ({
    currentDate,
    activities,
    isLoading,
    refetch,
    dataActivitiesTypes,
    dataTrucks,
    dataUsers,
    checkEditableRow,
    isActivitiesTypesLoading,
    isUsersLoading,
    site,
    aggridRefs,
    siteRefs,
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const createActivityNeedsModal = useBoolean(false);

    const { mutate: mutateActivitiesState, isLoading: isActivitiesStateLoading } = useActivitiesStateMutation();
    const { mutate: submitNeeds, isLoading: isSubmitNeedsLoading } = useImportActivitiesBySiteMutation({ onSettled: () => refetch() });
    const { mutate: updateActivity } = useUpdateActivityMutation({ onSuccess: () => refetch() });
    const { mutate: deleteActivity } = useDeleteActivityMutation({ onSettled: () => refetch() });

    const setSpecificHour = () => {
        const date = new Date(currentDate);
        date.setHours(7, 0);
        return date.toISOString();
    };

    const checkSiteState = (siteId: string): number => {
        let tmpState = 0;
        const tmpActivities = activities?.filter((value) => value.siteId._id === siteId);
        if (!tmpActivities || tmpActivities.length === 0) return activityStateEnum.NEEDS;
        for (let i = 0; i < tmpActivities.length; i++) {
            if (tmpActivities[i].activityState > tmpState) tmpState = tmpActivities[i].activityState;
        }
        return tmpState;
    };

    const columnDefs: ColDef<DataItem>[] = [
        {
            headerName: capitalizeFirstLetter(t('person')),
            valueGetter: (params) => formatName(params.data?.reportId.userId.firstName, params.data?.reportId.userId.lastName),
            colId: 'name',
            pinned: 'left',
            cellClass: 'ag-cell-remove-of',
            width: 250,
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data) return params.value;

                return (
                    <PersonNeedsCell
                        activity={params.data}
                        users={dataUsers}
                        activities={activities || []}
                        disabled={!checkEditableRow(params.data)}
                        refetch={refetch}
                        loading={isUsersLoading}
                    />
                );
            },
        },
        {
            headerName: capitalizeFirstLetter(t('start')),
            colId: 'start',
            width: 100,
            valueGetter: (params) => !!params.data && formatTime(new Date(params.data?.startTime)),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data) return params.value;
                return <StartTimeCell activity={params.data} refetch={refetch} disabled={!checkEditableRow(params.data)} />;
            },
        },
        {
            headerName: capitalizeFirstLetter(t('machines')),
            colId: 'machines',
            width: 300,
            cellStyle: { overflow: 'visible' },
            cellClass: 'ag-cell-remove-of',
            valueGetter: (params) => params.data?.dailyMachines.map((m) => m.machineId.designation),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return (
                    <MachinesCell
                        activity={params.data}
                        activityIndex={params.node.rowIndex}
                        activities={activities}
                        disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                        refetch={refetch}
                        createTransfer={false}
                    />
                );
            },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('tools')),
            colId: 'tools',
            width: 300,
            cellClass: 'ag-cell-remove-of',
            valueGetter: (params) => params.data?.dailyTools.map((t) => t.toolId.designation),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return (
                    <ToolsCell
                        activity={params.data}
                        activityIndex={params.node.rowIndex}
                        activities={activities}
                        disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                        refetch={refetch}
                        createTransfer={false}
                    />
                );
            },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('rentals')),
            colId: 'rentals',
            width: 300,
            cellClass: 'ag-cell-remove-of',
            valueGetter: (params) => params.data?.dailyRentals.map((r) => `${r.designation} ${r.rentalTypeId.designation}`.trim()),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return (
                    <RentalsCell
                        activity={params.data}
                        activityIndex={params.node.rowIndex}
                        activities={activities}
                        disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                        refetch={refetch}
                        createTransfer={false}
                    />
                );
            },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: t('trucks'),
            width: 280,
            cellClass: 'ag-cell-remove-of',
            valueGetter: (params) =>
                params.data?.dailyTrucksPlanned.map((tp) =>
                    `${tp.trucksAmount} ${tp.truckCompanyId?.designation} ${tp.ridesAmount || ''} ${getTruckModeLabel(tp.mode)} ${tp.truckId?.designation}`.trim()
                ),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return (
                    <TrucksPlannedCell
                        activity={params.data}
                        activityIndex={params.node.rowIndex}
                        disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type !== activityTypeEnum.TRUCK}
                        refetch={refetch}
                        stepMode={stepModeEnum.NEEDS}
                        activities={activities || []}
                        currentDate={currentDate}
                    />
                );
            },
            colId: 'trucks',
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: t('observations'),
            field: 'observationsPlanning',
            minWidth: 300,
            initialFlex: 1,
            editable: (params) => !!params.data && checkEditableRow(params.data),
            colId: 'observations',

            valueFormatter: (params) => params.value || '',

            valueSetter: (params) => {
                const newValue = params.newValue?.trim() || '';
                const oldValue = params.data.observationsPlanning?.trim() || '';
                if (newValue !== oldValue) {
                    updateActivity(
                        { id: params.data._id, body: { observationsPlanning: newValue } },
                        {
                            onSettled: () => {
                                refetch();
                            },
                        }
                    );
                    params.data.observationsPlanning = newValue;
                }
                return true;
            },

            cellEditor: 'agLargeTextCellEditor',
            cellEditorPopup: true,
            cellStyle: { whiteSpace: 'pre' },
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('tempWorkers')),
            colId: 'tempworkers',
            cellClass: 'ag-cell-remove-of',
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
            valueGetter: (params) =>
                (params.data?.dailyTempWorkers || []).map((tw) => `${tw?.tempWorkerTypeId?.designation || ''} ${tw?.durationHours || ''}h ${tw?.tempWorkerName || ''}`.trim()),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return (
                    <TempWorkersCell
                        activity={params.data}
                        activityIndex={params.node.rowIndex}
                        activities={activities}
                        disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                        refetch={() => refetch()}
                    />
                );
            },
        },
        {
            headerName: capitalizeFirstLetter(t('subContractors')),
            colId: 'subcontractors',
            cellClass: 'ag-cell-remove-of',
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
            valueGetter: (params) => (params.data?.dailySubContractors || []).map((sc) => `${sc?.subContractorTypeId?.designation || ''}`.trim()),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data || typeof params.node.rowIndex !== 'number') return null;
                return (
                    <SubContractorsCell
                        activity={params.data}
                        activityIndex={params.node.rowIndex}
                        activities={activities}
                        disabled={!checkEditableRow(params.data) || params.data.activityTypeId?.type === activityTypeEnum.TRUCK}
                        refetch={() => refetch()}
                    />
                );
            },
        },
        {
            headerName: t('truckManager'),
            field: 'isTruckManager',
            width: 100,
            colId: 'truckmanager',
            cellRendererSelector: (params) => {
                if (params.data?.activityTypeId?.type === activityTypeEnum.TRUCK) {
                    const truckType = dataTrucks?.find((value) => value.userId?._id === params.data?.reportId?.userId?._id)?.truckTypeId?.designation;

                    return {
                        component: () => <TruckIcon type={(truckType || '') as 'Semie' | 'Ampliroll' | '8x4' | 'Porte-char' | ''} dark />,
                    };
                } else {
                    return {
                        component: 'agCheckboxCellRenderer',
                    };
                }
            },
        },
        {
            headerName: capitalizeFirstLetter(t('activity')),
            field: 'activityTypeId.designation',
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data) return params.value;

                return (
                    <ActivityNeedsCell
                        activitiesTypes={dataActivitiesTypes}
                        activity={params.data}
                        refetch={refetch}
                        disabled={!checkEditableRow(params.data)}
                        isLoading={isActivitiesTypesLoading}
                    />
                );
            },
            colId: 'activitytype',
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getActivityStateLabel(params.data?.activityState))),
            cellRenderer: (params: ICellRendererParams<DataItem>) => {
                if (!params.data) return params.value;
                return (
                    <StateCell
                        stateColor={getActivityStateColor(params.data?.activityState)}
                        text={t(getActivityStateLabel(params.data?.activityState))}
                    />
                );
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            initialWidth: 150,
            colId: 'actions',
            filter: false,
            sortable: false,
            enableRowGroup: false,
            enableValue: false,
            cellRenderer: (params: ICellRendererParams<DataItem>) => (
                <div>
                    {!!user && (user?._id === params.data?.siteId.conducId?._id || user?._id === params.data?.siteId.assistantConducId?._id) && (
                        <IconLine>
                            {params.data?.activityState === activityStateEnum.NEEDSFILLED && (
                                <IconButton
                                    iconName="publishedWithChanges"
                                    onClick={() => {
                                        mutateActivitiesState(
                                            {
                                                body: {
                                                    activityIds: [params.data?._id || ''],
                                                    activityState: activityStateEnum.NEEDS,
                                                    currentState: activityStateEnum.NEEDSFILLED,
                                                },
                                            },
                                            { onSettled: () => refetch() }
                                        );
                                    }}
                                    title={capitalizeFirstLetter(t('validate'))}
                                    color="warning"
                                    loading={isActivitiesStateLoading}
                                />
                            )}
                            {params.data?.activityState === activityStateEnum.NEEDS && (
                                <IconButton
                                    iconName="done"
                                    onClick={() => {
                                        mutateActivitiesState(
                                            {
                                                body: {
                                                    activityIds: [params.data?._id || ''],
                                                    activityState: activityStateEnum.NEEDSFILLED,
                                                    currentState: activityStateEnum.NEEDS,
                                                },
                                            },
                                            { onSettled: () => refetch() }
                                        );
                                    }}
                                    title={capitalizeFirstLetter(t('cancel'))}
                                    loading={isActivitiesStateLoading}
                                />
                            )}
                            {params.data?.activityState === activityStateEnum.NEEDS && (
                                <IconButton
                                    iconName="deleteForever"
                                    onClick={() => deleteActivity({ id: params.data?._id || '' })}
                                    title={capitalizeFirstLetter(t('deleteActivity'))}
                                    color="error"
                                />
                            )}
                        </IconLine>
                    )}
                </div>
            ),
        },
        {
            headerName: capitalizeFirstLetter(t('sendAt')),
            field: 'needSubmitValidationTimeStamp',
            valueFormatter: ({ value }) => (value ? `${formatDateToDDMMYYYY(new Date(value), '/')} ${formatTime(new Date(value))}` : ''),
            width: 150,
            colId: 'sendAt',
        },
        ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<DataItem>[]),
    ];

    return (
        <>
            <GenericAgGridTable
                key={site._id}
                gridRef={(el) => (aggridRefs.current[site._id] = el)}
                containerRef={(el) => (siteRefs.current[site._id] = el)}
                rowData={activities}
                columnDefs={columnDefs}
                agGridProps={{
                    getRowId: (params) => params.data._id,
                    domLayout: 'autoHeight',
                }}
                showSearch={false}
                loading={isLoading}
                barLineColor={site.conducId?.color}
                headerLeftComponent={
                    <>
                        <div className="">
                            <div className="fx-r lFont font-w-700 gap-10px fx-items-c">
                                {site.name}
                                <div className="fx-r gap-5px fx-items-c">
                                    <div
                                        style={{
                                            backgroundColor: site.conducId?.color,
                                            height: 8,
                                            width: 8,
                                            borderRadius: 5,
                                        }}
                                    />
                                    <div className="font-w-600 mFont">{formatName(site.conducId?.firstName, site.conducId?.lastName)}</div>
                                </div>
                                <div className="fx-r gap-5px fx-items-c ">
                                    <div
                                        style={{
                                            backgroundColor: site.assistantConducId?.color,
                                            height: 8,
                                            width: 8,
                                            borderRadius: 5,
                                        }}
                                    />
                                    <div className="font-w-600 mFont">
                                        {formatName(site.assistantConducId?.firstName, site.assistantConducId?.lastName)}
                                    </div>
                                </div>
                            </div>
                            {/*<div>
                                                <div className="fx-r gap-10px fx-items-bl">
                                                    <div className="xlFont font-w-700">
                                                        {
                                                            sortedActivities(site._id).filter(
                                                                (activity) => activity.activityState === activityStateEnum.NEEDSIMPORTED
                                                            ).length
                                                        }
                                                        /
                                                        {
                                                            sortedActivities(site._id).filter(
                                                                (activity) => activity.activityState === activityStateEnum.NEEDSSENT
                                                            ).length
                                                        }
                                                        /{sortedActivities(site._id).length}
                                                    </div>
                                                    <div className="text-gray font-w-600">{t('activitie(s)exported/sent/total')}</div>
                                                </div>
                                            </div>*/}
                        </div>
                    </>
                }
                headerRightComponent={
                    <>
                        {!!user &&
                            (user._id === site.conducId?._id || user._id === site.assistantConducId?._id) &&
                            checkSiteState(site._id) >= activityStateEnum.NEEDSFILLED && (
                                <>
                                    <IconButton
                                        iconName="send"
                                        onClick={() => {
                                            submitNeeds({
                                                siteId: site._id,
                                                date: currentDate.toISOString(),
                                                fromState: activityStateEnum.NEEDSFILLED,
                                                toState: activityStateEnum.NEEDSSENT,
                                                stepMode: stepModeEnum.NEEDS,
                                            });
                                        }}
                                        title={capitalizeFirstLetter(t('sendNeeds'))}
                                        disabled={
                                            activities.reduce(
                                                (sum, item) => (sum + item.activityState !== activityStateEnum.NEEDSFILLED ? 1 : 0),
                                                0
                                            ) > 0
                                        }
                                        loading={isSubmitNeedsLoading}
                                    />
                                    <IconButton
                                        iconName="addCircle"
                                        onClick={() => {
                                            createActivityNeedsModal.setTrue();
                                        }}
                                        title={capitalizeFirstLetter(t('addActivity'))}
                                    />
                                </>
                            )}
                        {!!user && user?.profileId.permsLevel <= permissionLevelEnum.PLANNING && <></>}
                    </>
                }
            />
            {createActivityNeedsModal.value && (
                <CreateActivityNeedsModal
                    open={createActivityNeedsModal.value}
                    handleClose={createActivityNeedsModal.setFalse}
                    refetch={refetch}
                    initDate={setSpecificHour()}
                    currentDate={currentDate}
                    stepMode={stepModeEnum.NEEDS}
                    activityState={activityStateEnum.NEEDS}
                    reportState={reportStateEnum.NEEDS}
                    initSiteId={site._id}
                />
            )}
        </>
    );
};

export default NeedsGlobalTable;
